import { useState, useEffect, useMemo } from "react";
import "./App.css";
import { BACKEND_URL, apiHeaders } from "./config";

export default function DashboardPaired() {
  const [products, setProducts] = useState([]);
  const [pairings, setPairings] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("Vše");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/produkty`, { headers: apiHeaders() }).then((r) => r.json()),
      fetch(`${BACKEND_URL}/api/parovani`, { headers: apiHeaders() }).then((r) => r.json()),
    ])
      .then(([prods, pairs]) => {
        setProducts(prods);
        setPairings(pairs);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  // Rychlé vyhledávání produktu podle ID
  const productById = useMemo(() => {
    const map = {};
    products.forEach((p) => { map[p.id] = p; });
    return map;
  }, [products]);

  // Seřazené e-shopy: Boppe první, zbytek abecedně
  const eshopy = useMemo(() => {
    const names = new Set(products.map((p) => p.eshop).filter(Boolean));
    const arr = Array.from(names);
    const boppe = arr.find((e) => e.includes("Boppe"));
    const rest = arr.filter((e) => !e.includes("Boppe")).sort();
    return boppe ? [boppe, ...rest] : arr.sort();
  }, [products]);

  // Kategorie z Boppe produktů (reference pro párování)
  const categories = useMemo(() => {
    const cats = new Set();
    Object.keys(pairings).forEach((boppeId) => {
      const p = productById[boppeId];
      if (p?.kategorie) cats.add(p.kategorie);
    });
    return ["Vše", ...Array.from(cats).sort()];
  }, [pairings, productById]);

  // Filtrovaná párování
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return Object.entries(pairings).filter(([boppeId, linkedIds]) => {
      const main = productById[boppeId];
      if (!main) return false;
      if (selectedCategory !== "Vše" && main.kategorie !== selectedCategory) return false;
      if (q) {
        const all = [main, ...linkedIds.map((id) => productById[id])].filter(Boolean);
        return all.some((x) => x.nazev?.toLowerCase().includes(q));
      }
      return true;
    });
  }, [pairings, productById, selectedCategory, search]);

  // E-shopy prodávající pletivo za metr (ne za balení/roli)
  const PER_METR_ESHOPY = ["Pletiva Dobry", "Pilecký", "MM Ploty", "E-Pletivo"];

  // Normalizuj cenu na cenu za balení (15m nebo 25m)
  // Pro per-metr e-shopy: délku hledáme v kategorii produktu, nebo záložně v kategorii Boppe
  const normalizujCenu = (p, boppeKategorie) => {
    const cena = parseFloat(p.cena);
    if (isNaN(cena) || cena <= 0) return null;
    if (!PER_METR_ESHOPY.includes(p.eshop)) return cena;
    const katZdroj = (p.kategorie || "") + " " + (boppeKategorie || "");
    if (katZdroj.includes("25m")) return +(cena * 25).toFixed(2);
    if (katZdroj.includes("15m")) return +(cena * 15).toFixed(2);
    return cena; // neznámé balení — zobrazíme bez úpravy
  };

  // Cenové porovnání — zelená = nejlevnější, Boppe oranžová/červená pokud dražší
  const getPriceInfo = (price, allPrices, isBoppe) => {
    const nums = allPrices.filter((x) => x != null).map(Number);
    if (!nums.length || price == null) return { cls: "price-missing", label: "–" };
    const val = Number(price);
    const min = Math.min(...nums);
    const label = `${val.toLocaleString("cs-CZ")} Kč`;
    if (Math.abs(val - min) < 0.01) return { cls: "green", label, badge: "✔" };
    if (!isBoppe) return { cls: "", label };
    const diff = ((val - min) / min) * 100;
    if (diff >= 10) return { cls: "red", label, badge: `+${Math.round(diff)} %` };
    if (diff >= 3) return { cls: "orange", label, badge: `+${Math.round(diff)} %` };
    return { cls: "", label };
  };

  if (loading) return <p className="loading-message">🔄 Načítání dat…</p>;
  if (error) return <p className="loading-message">❌ Chyba: {error}</p>;
  if (!Object.keys(pairings).length)
    return <p className="loading-message">Žádná spárovaná data. Nejdříve spáruj produkty.</p>;

  return (
    <div className="paired-layout">
      {/* Sidebar — kategorie */}
      <aside className="sidebar">
        <ul>
          {categories.map((cat) => (
            <li key={cat}>
              <button
                className={`category-toggle ${selectedCategory === cat ? "active" : ""}`}
                onClick={() => setSelectedCategory(cat)}
                title={cat}
              >
                <span className="category-icon">{cat === "Vše" ? "📋" : "📂"}</span>
                <span className="category-name">{cat}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      {/* Hlavní obsah */}
      <main className="paired-main">
        {/* Header */}
        <div className="paired-header">
          <div className="paired-title">
            <h1>📊 Přehled cen konkurence</h1>
            <span className="paired-count">{filtered.length} párování</span>
          </div>
          <input
            className="paired-search"
            type="text"
            placeholder="🔍 Hledat produkt…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Tabulka */}
        <div className="table-container">
          {filtered.length === 0 ? (
            <p className="empty-message">Žádná párování v této kategorii.</p>
          ) : (
            <table className="styled-table">
              <thead>
                <tr>
                  <th className="col-produkt">Produkt</th>
                  {eshopy.map((e) => (
                    <th key={e}>{e}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(([boppeId, linkedIds]) => {
                  const all = [productById[boppeId], ...linkedIds.map((id) => productById[id])].filter(Boolean);
                  const boppe = all.find((p) => p.eshop?.includes("Boppe")) || all[0];

                  // eshop → product
                  const byEshop = {};
                  all.forEach((p) => { byEshop[p.eshop] = p; });

                  // ceny pro porovnání — normalizované (per-metr e-shopy × délku balení)
                  const prices = eshopy.map((e) => {
                    const p = byEshop[e];
                    if (!p) return null;
                    return normalizujCenu(p, boppe?.kategorie);
                  });

                  return (
                    <tr key={boppeId}>
                      <td className="col-produkt">
                        <a href={boppe?.url || "#"} target="_blank" rel="noopener noreferrer">
                          {boppe?.nazev || "?"}
                        </a>
                        {boppe?.varianta && boppe.varianta !== "standard" && (
                          <span className="varianta-badge">{boppe.varianta}</span>
                        )}
                        <span className="kategorie-badge">{boppe?.kategorie}</span>
                      </td>
                      {eshopy.map((eshop) => {
                        const p = byEshop[eshop];
                        const isBoppe = eshop?.includes("Boppe");
                        if (!p) return <td key={eshop} className="price-cell price-missing">–</td>;

                        const normalizedPrice = normalizujCenu(p, boppe?.kategorie);
                        const { cls, label, badge } = getPriceInfo(normalizedPrice, prices, isBoppe);
                        return (
                          <td key={eshop} className={`price-cell ${cls}`}>
                            <a href={p.url || "#"} target="_blank" rel="noopener noreferrer">
                              {label}
                            </a>
                            {badge && <span className="price-badge">{badge}</span>}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Legenda */}
        <div className="paired-legend">
          <span className="legend-item green">✔ nejlevnější</span>
          <span className="legend-item orange">+3–9 % dražší (Boppe)</span>
          <span className="legend-item red">+10 %+ dražší (Boppe)</span>
          <span className="legend-item price-missing">– nespárováno</span>
        </div>
      </main>
    </div>
  );
}
